import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { BellRing, Loader2, RefreshCw, Send, Store, Users } from 'lucide-react';
import api from '../../lib/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const Pill = ({ ok, children, testid }) => (
  <Badge data-testid={testid}
         className={ok ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}>{children}</Badge>
);

const REASON_COPY = {
  no_token: 'her device never registered',
  pref_filter: 'she turned this category off',
  transport: 'FCM rejected the token (check the APNs key)',
  unknown_event: 'unknown event type — a bug',
  ok: 'delivered',
};

export const PushStoreReadinessCard = () => {
  const [push, setPush] = useState(null);
  const [store, setStore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [dedupeResult, setDedupeResult] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [p, s] = await Promise.all([
        api.get('/api/admin/ops/push-health'),
        api.get('/api/admin/ops/store-readiness'),
      ]);
      setPush(p.data); setStore(s.data);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dedupe = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/admin/ops/push-health/dedupe-tokens');
      setDedupeResult(r.data);
      load();
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setResult(null); setBusy(true);
    try {
      const r = await api.post('/api/admin/ops/push-health/test', { query });
      setResult(r.data);
      load();
    } catch (e) {
      setResult({ ok: false, code: e?.response?.data?.detailObject?.code || 'FAILED',
                  hint: e?.response?.data?.detailObject?.query ? 'No learner matched that email / phone / id' : '' });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border p-4 space-y-4" data-testid="push-store-readiness-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <BellRing className="h-4 w-4 text-primary" />Push health &amp; store readiness
        </h3>
        <Button size="sm" variant="outline" className="ml-auto" onClick={load} disabled={busy}
                data-testid="push-store-refresh">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1">Refresh</span>
        </Button>
      </div>

      {/* ── push ─────────────────────────────────────────────── */}
      {push && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Pill ok={push.fcm_ready} testid="push-fcm-ready">{push.fcm_ready ? 'FCM connected' : 'FCM not configured'}</Pill>
            <Pill ok={push.tokens.total > 0} testid="push-tokens-pill">
              {push.tokens.total} device token(s) · {push.tokens.users} learner(s)
            </Pill>
            <span className="text-xs text-muted-foreground" data-testid="push-platforms">
              iOS {push.tokens.by_platform.ios} · Android {push.tokens.by_platform.android} · Web {push.tokens.by_platform.web}
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs text-muted-foreground">Send a test push to (email / phone / user id)</label>
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder="mother@example.com" data-testid="push-test-input" />
            </div>
            <Button size="sm" onClick={sendTest} disabled={busy || !query.trim()} data-testid="push-test-send">
              <Send className="h-3.5 w-3.5 mr-1" />Send test push
            </Button>
            <Button size="sm" variant="outline" onClick={dedupe} disabled={busy} data-testid="push-dedupe-btn">
              <Users className="h-3.5 w-3.5 mr-1" />Clean shared-device tokens
            </Button>
          </div>

          {dedupeResult && (
            <p className="text-xs text-muted-foreground" data-testid="push-dedupe-result">
              {dedupeResult.rows_removed} stale token row(s) removed across {dedupeResult.duplicate_tokens} shared
              device(s) · {dedupeResult.tokens_total} token(s) now registered
            </p>
          )}

          {result && (
            <div className={`text-sm rounded-xl border p-2 ${result.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                 data-testid="push-test-result">
              <p className="font-medium">
                {result.ok ? `sent=${result.sent} — it landed on her device` : `Not sent (${result.code || 'NO_TOKEN'})`}
              </p>
              {result.name && <p className="text-xs">{result.name} · {result.tokens || 0} token(s) · {(result.platforms || []).join(', ')}</p>}
              {result.hint && <p className="text-xs text-muted-foreground">{result.hint}</p>}
            </div>
          )}

          <div className="rounded-xl border divide-y" data-testid="push-recent-list">
            {(push.recent || []).length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground" data-testid="push-recent-empty">No pushes dispatched yet.</p>
            ) : push.recent.slice(0, 8).map((r, i) => (
              <div key={i} className="px-3 py-1.5 flex flex-wrap items-center gap-x-3 text-xs" data-testid={`push-row-${i}`}>
                <span className="font-medium min-w-[150px]">{r.event_type}</span>
                <Badge className={`text-[10px] ${r.status === 'sent' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                  {r.status === 'sent' ? `sent ${r.sent}` : r.status}
                </Badge>
                <span className="text-muted-foreground">{REASON_COPY[r.reason] || r.reason}</span>
                <span className="ml-auto text-muted-foreground">{fmt(r.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── store ────────────────────────────────────────────── */}
      {store && (
        <div className="space-y-2 pt-2 border-t">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="flex items-center gap-1.5 font-medium"><Store className="h-4 w-4 text-primary" />Store</span>
            <Pill ok={store.configured?.webhook_auth} testid="store-webhook-pill">
              {store.configured?.webhook_auth ? 'Webhook auth set' : 'Webhook auth missing'}
            </Pill>
            <Pill ok={store.configured?.secret_api_key} testid="store-key-pill">
              {store.configured?.secret_api_key ? 'RC secret key set' : 'RC secret key missing'}
            </Pill>
            <Pill ok={!store.configured?.allow_sandbox_grants} testid="store-sandbox-pill">
              {store.configured?.allow_sandbox_grants ? 'Sandbox grants ON (unsafe for live)' : 'Sandbox grants off'}
            </Pill>
            <Pill ok={store.apple_siwa_revoke?.configured} testid="store-siwa-revoke-pill">
              {store.apple_siwa_revoke?.configured
                ? 'Apple sign-in revoke key set'
                : `Apple revoke key missing (${(store.apple_siwa_revoke?.missing || []).join(', ')})`}
            </Pill>
            <span className="text-xs text-muted-foreground" data-testid="store-map-size">
              {store.product_map_size} product id(s) mapped · entitlement {store.configured?.entitlement_id} · project {store.configured?.project_id}
            </span>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="store-apple-client-id">
            Apple revoke client id: <span className="font-mono">{store.apple_siwa_revoke?.client_id || '—'}</span>
            {store.apple_siwa_revoke?.key_id ? ` · key ${store.apple_siwa_revoke.key_id}` : ''}
          </p>
          {store.apple_siwa_revoke?.warning && (
            <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 p-2" data-testid="store-apple-bundle-warning">
              {store.apple_siwa_revoke.warning}
            </p>
          )}
          <p className="text-sm" data-testid="store-real-events">
            {store.real_store_events > 0
              ? `${store.real_store_events} real store event(s) received — last: ${store.last_real_event?.type} · ${store.last_real_event?.product_id || '—'} · ${fmt(store.last_real_event?.received_at)}`
              : 'No real store purchase has reached the LMS yet.'}
          </p>
          <div className="rounded-xl border divide-y" data-testid="store-events-list">
            {(store.recent_events || []).length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground" data-testid="store-events-empty">No store events yet.</p>
            ) : store.recent_events.slice(0, 6).map((e, i) => (
              <div key={i} className="px-3 py-1.5 flex flex-wrap items-center gap-x-3 text-xs" data-testid={`store-event-${i}`}>
                <span className="font-medium min-w-[130px]">{e.type}</span>
                <span className="text-muted-foreground">{e.product_id || '—'}</span>
                {e.environment && <Badge className="text-[10px] bg-slate-200 text-slate-700">{e.environment}</Badge>}
                {e.result && <span className="text-muted-foreground">{e.result}</span>}
                <span className="ml-auto text-muted-foreground">{fmt(e.received_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
